/**
 * Zentraler Link-Builder für Hub und Workflow-URLs.
 * Alle Links müssen hierher – keine String-Konkatenation in Pages.
 */

/** @typedef {'open'|'new'|'stats'|'settings'} TournamentIntent */

function __asStr(x) {
  return String(x ?? "").trim();
}

/** kind -> status -> route name (ohne leading slash) */
const ROUTES_BY_KIND = {
  /** Persistenz/Server liefern weiter „schleifchen“ als Turnier-Art */
  schleifchen: {
    planned: "matchmixer-setup",
    running: "matchmixer",
    finished: "matchmixer-finish",
    default: "matchmixer",
  },
  matchmixer: {
    planned: "matchmixer-setup",
    running: "matchmixer",
    finished: "matchmixer-finish",
    default: "matchmixer",
  },
  clubmeisterschaft: {
    planned: "clubmeisterschaft-setup",
    running: "clubmeisterschaft",
    finished: "clubmeisterschaft-finish",
    default: "clubmeisterschaft",
  },
  medenspiel: {
    planned: "medenspiel-setup",
    running: "medenspiel-running",
    finished: "medenspiel-finish",
    default: "medenspiel-running",
  },
};

/** Route name -> URL path (wie in core/router/legacy-adapter + Schleifchen) */
const ROUTE_TO_PATH = {
  "matchmixer-setup": "/matchmixer/setup",
  "matchmixer-setup-players": "/matchmixer/setup/players",
  "matchmixer-schedule": "/matchmixer/schedule",
  "matchmixer-finish": "/matchmixer/finish",
  "matchmixer-entry": "/matchmixer/entry",
  "matchmixer-results": "/matchmixer/results",
  "matchmixer-round-config": "/matchmixer/round-config",
  matchmixer: "/matchmixer",
  "schleifchen-setup": "/matchmixer/setup",
  "schleifchen-schedule": "/matchmixer/schedule",
  "schleifchen-finish": "/matchmixer/finish",
  "schleifchen-entry": "/matchmixer/entry",
  "schleifchen-results": "/matchmixer/results",
  "schleifchen-round-config": "/matchmixer/round-config",
  schleifchen: "/matchmixer",
  "clubmeisterschaft-setup": "/clubmeisterschaft/setup",
  clubmeisterschaft: "/clubmeisterschaft",
  "clubmeisterschaft-schedule": "/clubmeisterschaft/schedule",
  "clubmeisterschaft-entry": "/clubmeisterschaft/entry",
  "clubmeisterschaft-finish": "/clubmeisterschaft/finish",
  medenspiele: "/medenspiele",
  "medenspiele-kader": "/medenspiele/kader",
  "medenspiele-termine": "/medenspiele/termine",
  "medenspiele-training": "/medenspiele/training",
  "medenspiele-planning": "/medenspiele/training",
  belegung: "/hub/belegung",
  kalender: "/hub/kalender",
  insights: "/hub/insights",
  statistik: "/hub/insights",
  matchflow: "/hub/matchflow",
  "hub-matchflow": "/hub/matchflow",
  minigames: "/minigames",
  trainerstunden: "/trainingsdesk/planung",
  trainingsdesk: "/trainingsdesk/planung",
  trainer: "/trainer",
  "hub-trainer": "/hub/trainer",
  hub: "/hub",
  "hub-matchmixer": "/hub/matchmixer",
  "hub-matchmixer-open": "/hub/matchmixer/open",
  "hub-matchmixer-statistik": "/hub/matchmixer/statistik",
  "hub-schleifchen": "/hub/matchmixer",
  "hub-belegung": "/hub/belegung",
  "hub-kalender": "/hub/kalender",
  "hub-insights": "/hub/insights",
  "matchmixer-anmelden": "/matchmixer/anmelden",
  "matchmixer-me": "/matchmixer/me",
};

function __routeForKindStatus(kindRaw, statusRaw) {
  const kind = __asStr(kindRaw).toLowerCase();
  const status = __asStr(statusRaw).toLowerCase();
  const kindMap = ROUTES_BY_KIND[kind] || null;
  if (!kindMap) return "home";
  return kindMap[status] || kindMap.default || "home";
}

function __routeToPath(route) {
  if (ROUTE_TO_PATH[route]) return ROUTE_TO_PATH[route];
  if (route.startsWith("matchmixer-")) {
    return `/matchmixer/${route.slice("matchmixer-".length)}`;
  }
  if (route.startsWith("schleifchen-")) {
    return `/matchmixer/${route.slice("schleifchen-".length)}`;
  }
  return route.startsWith("/") ? route : `/${route}`;
}

/**
 * Hub-URL für eine App.
 * @param {string} appKey - z.B. "schleifchen", "trainerstunden", "clubmeisterschaft", "medenspiele"
 * @returns {string} z.B. "/hub/schleifchen", "/hub"
 */
export function buildAppHref(appKey) {
  const key = __asStr(appKey);
  if (!key) return "/hub";
  if (key === "schleifchen" || key === "matchmixer") return "/hub/matchmixer";
  if (key === "trainerstunden") return "/trainingsdesk/planung";
  return `/hub/${key}`;
}

/**
 * Marketplace App-Detail (v0.3): nur Metadaten-Seite, kein Loader.
 * @param {string} appKey
 * @returns {string} z.B. "/hub/apps/schleifchen"
 */
export function buildMarketplaceAppDetailHref(appKey) {
  const key = __asStr(appKey);
  if (!key) return "/hub";
  return `/hub/apps/${encodeURIComponent(key)}`;
}

/**
 * Workflow-URL für ein Turnier (Open/New/Stats/Settings).
 * Kanonische MatchMixer-Pfade: /matchmixer/setup, /matchmixer/schedule, … (Legacy-Redirects im Host).
 *
 * @param {object} opts
 * @param {string} opts.appKey - "schleifchen" | "clubmeisterschaft" | …
 * @param {string} [opts.code] - Turnier-Code (bei intent "open" erforderlich)
 * @param {TournamentIntent} opts.intent - "open" | "new" | "stats" | "settings"
 * @param {string} [opts.kind] - Turnier-Art (z.B. "schleifchen"), bei "open" für Routenlogik
 * @param {string} [opts.status] - "planned"|"running"|"finished", bei "open" für Routenlogik
 * @returns {string} absolute path (mit ?code= bei "open")
 */
export function buildTournamentHref({
  appKey,
  code,
  intent,
  kind,
  status,
  setupIntent,
}) {
  const key = __asStr(appKey);
  const i = String(intent || "open").toLowerCase();

  if (i === "new") {
    if (key === "schleifchen" || key === "matchmixer") {
      const si = String(setupIntent || "play")
        .trim()
        .toLowerCase();
      const intentQ = si === "register" ? "register" : "play";
      return `/matchmixer/setup?new=1&intent=${encodeURIComponent(intentQ)}`;
    }
    if (key === "clubmeisterschaft") return "/clubmeisterschaft/setup?new=1";
    return ROUTE_TO_PATH[key] ? `${ROUTE_TO_PATH[key]}?new=1` : `/${key}`;
  }

  if (i === "stats" || i === "settings") {
    return "#";
  }

  if (i === "open") {
    const c = __asStr(code);
    if (!c) return "#";
    const route = __routeForKindStatus(kind || key, status);
    const path = __routeToPath(route);
    const q = `?code=${encodeURIComponent(c)}`;
    return path.startsWith("/") ? `${path}${q}` : `/${path}${q}`;
  }

  return "#";
}

/**
 * Spieler-Anmeldung für ein offenes Turnier.
 * @param {string} code
 * @returns {string}
 */
export function buildMatchmixerRegistrationHref(code) {
  const c = __asStr(code);
  if (!c) return "/hub/matchmixer/open";
  return `/matchmixer/anmelden/${encodeURIComponent(c)}`;
}

/**
 * Teilnehmer-Ansicht eines laufenden Turniers (Spielplan, Ergebnisse).
 * @param {string} code
 * @returns {string}
 */
export function buildMatchmixerParticipantHref(code) {
  const c = __asStr(code);
  if (!c) return "/hub/matchmixer";
  return `/matchmixer?code=${encodeURIComponent(c)}`;
}

/**
 * MatchMixer-Setup bearbeiten (bestehendes Turnier).
 * @param {string} code
 * @returns {string}
 */
export function buildMatchmixerSetupHref(code) {
  const c = __asStr(code);
  if (!c) return "/matchmixer/setup";
  return `/matchmixer/setup?code=${encodeURIComponent(c)}`;
}
